// Typed API client for the costume-tool Worker.
// Throws on transport errors; returns the structured ApiResp envelope so
// callers can switch on `.ok`.

import type {
  ApiResp,
  Me,
  Health,
  UsersListResponse,
  Product,
  ProductsListResponse,
  ProductsListParams,
  VariantQtyBody,
  VariantQtyResult,
  ProductStatusBody,
  ProductStatusResult,
  BulkBody,
  BulkResult,
  UpdatePriceResult,
  SyncLogsListParams,
  SyncLogsListResponse,
  AlertsListParams,
  AlertsListResponse,
  AlertsUnreadCount,
  SingleProductResponse,
  CoChildrenResponse,
  RefreshProductResponse,
  RefreshChannelPageResponse,
  MabangImportRow,
  MabangImportResult,
  MabangStats,
  MabangSearchResponse,
  // Stage 2 W3 ---
  FoldersListResponse,
  JobDetail,
  JobListResponse,
  JobListParams,
  GenerateJobBody,
  GenerateJobResponse,
  CancelJobResponse,
  PublishJobResponse,
  // Stage E4 — mappings ---
  MappingStats,
  MappingBucketResponse,
  MappingSuggestionResponse,
  MappingRecentResponse,
  SearchCandidatesResponse,
  LinkBody,
  LinkResult,
  UnlinkResult,
  VariantDeleteResult,
  ConfirmNoMatchResult,
  RerunMatchResult,
} from './types';

async function fetchJson<T>(path: string, init?: RequestInit): Promise<ApiResp<T>> {
  const res = await fetch(path, {
    credentials: 'include', // Cloudflare Access JWT cookie
    ...init,
  });
  // Even on 4xx/5xx the Worker returns the standard envelope; just parse + return.
  return (await res.json()) as ApiResp<T>;
}

function postJson<T>(path: string, body: unknown): Promise<ApiResp<T>> {
  return fetchJson<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export const api = {
  me: () => fetchJson<Me>('/api/me'),
  health: () => fetchJson<Health>('/api/health'),

  /**
   * Every CO child of this product straight from Magento — including the ones
   * the `variants` table can't hold (multi-axis products store only one child
   * per size). Ground truth for the per-child qty editor.
   */
  listCoChildren: (masterSku: string) =>
    fetchJson<CoChildrenResponse>(`/api/products/${encodeURIComponent(masterSku)}/co-children`),

  /**
   * Set qty on ONE exact CO child. Never fans out to same-size siblings —
   * that's what makes "the wig ran out, zero every +wig combo but keep
   * costume-only on sale" possible.
   */
  setCoChildQty: (masterSku: string, sku: string, qty: number) =>
    postJson<{ master_sku: string; sku: string; qty: number; summary: string | null }>(
      `/api/products/${encodeURIComponent(masterSku)}/co-children/qty`,
      { sku, qty },
    ),

  /** GET /api/users — owner-only list of the D1 users table. */
  listUsers: () => fetchJson<UsersListResponse>('/api/users'),

  /** GET /api/products with all the filter params from spec block 6.1. */
  listProducts: (params: ProductsListParams = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.channel) q.set('channel', params.channel);
    if (params.license) q.set('license', params.license);
    if (params.match_status?.length) q.set('match_status', params.match_status.join(','));
    if (params.status?.length) q.set('status', params.status.join(','));
    if (params.hide_disabled) q.set('hide_disabled', '1');
    if (params.updated_after) q.set('updated_after', params.updated_after);
    if (params.updated_before) q.set('updated_before', params.updated_before);
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    if (params.offset !== undefined) q.set('offset', String(params.offset));
    const qs = q.toString();
    return fetchJson<ProductsListResponse>(`/api/products${qs ? '?' + qs : ''}`);
  },

  /** Convenience: get just the total count for a filter combo. */
  countProducts: async (params: ProductsListParams = {}): Promise<number | null> => {
    const r = await api.listProducts({ ...params, limit: 1, offset: 0 });
    return r.ok ? r.data.total_products : null;
  },

  /** POST /api/variants/:id/qty — spec 6.3. */
  updateVariantQty: (variantId: number, body: VariantQtyBody) =>
    postJson<VariantQtyResult>(`/api/variants/${variantId}/qty`, body),

  /**
   * 解除一行变体的 CO 映射（Kids 侧保留）。用于 variants 行指向的
   * magento_sku 在 CO 上已经不是该父商品子商品的脏数据。
   */
  unlinkVariantCo: (variantId: number) =>
    postJson<{ variant_id: number; master_sku: string; size_key: string; cleared_magento_sku: string }>(
      `/api/variants/${variantId}/unlink-co`, {}),

  /**
   * 删掉一行幽灵变体（D1 only，不碰任何渠道）。
   * `confirm` 不传 = dry-run：只回判定和证据，不删。
   * 服务端会实时查 Kids/CO 证明这行确实对不到线上实体；只要有一侧
   * 「还在卖」或「没查成」，就返回 409 拒绝，details 里带完整判定。
   */
  deleteVariantRow: (variantId: number, confirm = false) =>
    postJson<VariantDeleteResult>(`/api/variants/${variantId}/delete`, { confirm }),

  /** POST /api/products/:master_sku/status — spec 6.4. */
  updateProductStatus: (masterSku: string, body: ProductStatusBody) =>
    postJson<ProductStatusResult>(
      `/api/products/${encodeURIComponent(masterSku)}/status`,
      body,
    ),

  /** POST /api/products/bulk — spec 6.5. */
  bulkAction: (body: BulkBody) => postJson<BulkResult>('/api/products/bulk', body),

  /** POST /api/products/update-price — 一处改价、同步 CO + eBay 两店 + Shopify Kids。 */
  updatePrice: (body: { master_sku: string; price: number; dry_run?: boolean }) =>
    postJson<UpdatePriceResult>('/api/products/update-price', body),

  /** GET /api/sync-logs — spec 6.12. */
  listSyncLogs: (params: SyncLogsListParams = {}) => {
    const q = new URLSearchParams();
    if (params.master_sku) q.set('master_sku', params.master_sku);
    if (params.variant_id !== undefined) q.set('variant_id', String(params.variant_id));
    if (params.actor) q.set('actor', params.actor);
    if (params.target_system?.length) q.set('target_system', params.target_system.join(','));
    if (params.action?.length) q.set('action', params.action.join(','));
    if (params.scope?.length) q.set('scope', params.scope.join(','));
    if (params.success !== undefined) q.set('success', params.success);
    if (params.from_ts) q.set('from_ts', params.from_ts);
    if (params.to_ts) q.set('to_ts', params.to_ts);
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    if (params.offset !== undefined) q.set('offset', String(params.offset));
    const qs = q.toString();
    return fetchJson<SyncLogsListResponse>(`/api/sync-logs${qs ? '?' + qs : ''}`);
  },

  /** GET /api/alerts. */
  listAlerts: (params: AlertsListParams = {}) => {
    const q = new URLSearchParams();
    if (params.dismissed) q.set('dismissed', params.dismissed);
    if (params.kind) q.set('kind', params.kind);
    if (params.master_sku) q.set('master_sku', params.master_sku);
    if (params.channel) q.set('channel', params.channel);
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    if (params.offset !== undefined) q.set('offset', String(params.offset));
    const qs = q.toString();
    return fetchJson<AlertsListResponse>(`/api/alerts${qs ? '?' + qs : ''}`);
  },

  /** GET /api/alerts/unread-count — lightweight nav badge. */
  alertsUnreadCount: () => fetchJson<AlertsUnreadCount>('/api/alerts/unread-count'),

  /** POST /api/alerts/:id/dismiss. */
  dismissAlert: (id: number) =>
    postJson<{ id: number; dismissed_at: string; dismissed_by: string }>(
      `/api/alerts/${id}/dismiss`,
      {},
    ),

  /** POST /api/alerts/dismiss-all — optional ids[] subset. */
  dismissAllAlerts: (ids?: number[]) =>
    postJson<{ dismissed_count: number }>(
      '/api/alerts/dismiss-all',
      ids ? { ids } : {},
    ),

  /** GET /api/products/:master_sku — spec 6.2. */
  getProduct: (masterSku: string) =>
    fetchJson<SingleProductResponse>(`/api/products/${encodeURIComponent(masterSku)}`),

  /** POST /api/refresh scope=product. Re-pulls one or more products from
   *  Shopify, runs match + upsert (which auto-runs disambiguator + sellout
   *  detection). */
  refreshProduct: (masterSkus: string[]) =>
    postJson<RefreshProductResponse>('/api/refresh', {
      scope: 'product',
      master_skus: masterSkus,
    }),

  /** POST /api/refresh scope=channel-page. Pulls one page (50 products max)
   *  from the channel and upserts them. Loop with the returned next_cursor
   *  until it's null to do a full refresh.
   *
   *  Optional `reconcile` opts the loop into stale-row reconciliation:
   *  when the final page returns (next_cursor=null), the server archives
   *  products with shopify_status='active' AND last_synced_at <
   *  sync_run_started_at. Pass the SAME ISO timestamp on every page of
   *  one logical refresh run (RefreshAllModal captures it at loop start). */
  refreshChannelPage: (
    cursor: string | null = null,
    limit = 50,
    channel: 'shopify' | 'magento' = 'shopify',
    reconcile?: { sync_run_started_at: string },
  ) =>
    postJson<RefreshChannelPageResponse>('/api/refresh', {
      scope: 'channel-page',
      channel,
      cursor,
      limit,
      ...(reconcile
        ? {
            reconcile_stale: true,
            sync_run_started_at: reconcile.sync_run_started_at,
          }
        : {}),
    }),

  /** GET /api/mabang/stats — header summary for /mabang page. */
  mabangStats: () => fetchJson<MabangStats>('/api/mabang/stats'),

  /** GET /api/mabang/search — substring lookup against normalized SKU. */
  mabangSearch: (q: string, limit = 50) => {
    const qs = new URLSearchParams({ q, limit: String(limit) }).toString();
    return fetchJson<MabangSearchResponse>(`/api/mabang/search?${qs}`);
  },

  /** POST /api/mabang/import — replace-mode bulk import. Pages parses xlsx
   *  client-side then POSTs the shaped rows. */
  mabangImport: (kind: 'simple' | 'combo', rows: MabangImportRow[]) =>
    postJson<MabangImportResult>('/api/mabang/import', { kind, rows }),

  // ============================================================
  // Stage 2 W3 — runner job endpoints
  // ============================================================

  /** GET /api/folders — list folders the Mac runner reports as available.
   *  Each folder includes sku_already_exists flag for UI gating. */
  listFolders: () => fetchJson<FoldersListResponse>('/api/folders'),

  /** POST /api/jobs/generate — submit a new generate job.
   *  `force` skips SKU_EXISTS + FOLDER_UNAVAILABLE pre-flight.
   *  `ackMabangWarning` skips MABANG_MISSING soft warning. */
  generateJob: (
    body: GenerateJobBody,
    opts: { force?: boolean; ackMabangWarning?: boolean } = {},
  ) => {
    const q = new URLSearchParams();
    if (opts.force) q.set('force', 'true');
    if (opts.ackMabangWarning) q.set('ack_mabang_warning', 'true');
    const qs = q.toString();
    return postJson<GenerateJobResponse>(
      `/api/jobs/generate${qs ? '?' + qs : ''}`,
      body,
    );
  },

  /** GET /api/jobs/:id — full detail + progress timeline. */
  getJob: (jobId: number) => fetchJson<JobDetail>(`/api/jobs/${jobId}`),

  /** GET /api/jobs — paginated list with filters. */
  listJobs: (params: JobListParams = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.master_sku) q.set('master_sku', params.master_sku);
    if (params.type) q.set('type', params.type);
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    if (params.offset !== undefined) q.set('offset', String(params.offset));
    const qs = q.toString();
    return fetchJson<JobListResponse>(`/api/jobs${qs ? '?' + qs : ''}`);
  },

  /** POST /api/jobs/:id/cancel — queued → cancelled, running → cancelling. */
  cancelJob: (jobId: number) =>
    postJson<CancelJobResponse>(`/api/jobs/${jobId}/cancel`, {}),

  /** POST /api/jobs/publish — enqueue an onboard/publish job for the Mac runner.
   *  channels default ['co','ebay']; idempotencyKey de-dups double-clicks (1h window). */
  publishJob: (masterSku: string, channels?: string[], idempotencyKey?: string) =>
    postJson<PublishJobResponse>('/api/jobs/publish', {
      master_sku: masterSku,
      ...(channels ? { channels } : {}),
      ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
    }),

  // ============================================================
  // Track E Phase E4 — mappings (manual link/unlink, re-run match)
  // ============================================================

  /** GET /api/mappings — bucket stat counts for the page header. */
  mappingStats: () => fetchJson<{ stats: MappingStats }>('/api/mappings'),

  /** GET /api/mappings?bucket=unmatched|low_confidence — list rows. */
  listMappingBucket: (bucket: 'unmatched' | 'low_confidence', limit = 50, offset = 0) => {
    const qs = new URLSearchParams({ bucket, limit: String(limit), offset: String(offset) }).toString();
    return fetchJson<MappingBucketResponse>(`/api/mappings?${qs}`);
  },

  /** GET /api/mappings?bucket=low_confidence — review suggestions (Kids ↔ proposed CO). */
  listSuggestions: (limit = 50, offset = 0) => {
    const qs = new URLSearchParams({ bucket: 'low_confidence', limit: String(limit), offset: String(offset) }).toString();
    return fetchJson<MappingSuggestionResponse>(`/api/mappings?${qs}`);
  },

  /** POST /api/mappings/dismiss-suggestion — drop a low-confidence suggestion. */
  dismissSuggestion: (masterSku: string) =>
    postJson<{ master_sku: string; dismissed: boolean }>('/api/mappings/dismiss-suggestion', { master_sku: masterSku }),

  /** GET /api/mappings/recently-linked — last-30-day decision audit. */
  recentlyLinked: (limit = 50, offset = 0) => {
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) }).toString();
    return fetchJson<MappingRecentResponse>(`/api/mappings/recently-linked?${qs}`);
  },

  /** GET /api/mappings/search-candidates — other-channel products for the link modal.
   *  want = the channel the unmatched product is MISSING (kids→need shopify, co→need magento). */
  searchLinkCandidates: (want: 'kids' | 'co', q: string, limit = 25) => {
    const qs = new URLSearchParams({ want, q, limit: String(limit) }).toString();
    return fetchJson<SearchCandidatesResponse>(`/api/mappings/search-candidates?${qs}`);
  },

  /** POST /api/mappings/link — manually link two channel rows. */
  linkProducts: (body: LinkBody) => postJson<LinkResult>('/api/mappings/link', body),

  /** POST /api/mappings/unlink — revert a manual link to unmatched. */
  unlinkProduct: (masterSku: string) =>
    postJson<UnlinkResult>('/api/mappings/unlink', { master_sku: masterSku }),

  /** POST /api/mappings/confirm-no-match — record single-channel product. */
  confirmNoMatch: (masterSku: string, channelPresent: 'kids' | 'co') =>
    postJson<ConfirmNoMatchResult>('/api/mappings/confirm-no-match', {
      master_sku: masterSku,
      channel_present: channelPresent,
    }),

  /** POST /api/mappings/rerun-match — name-fuzzy auto-link across unmatched. */
  rerunMatch: (masterSkus?: string[]) =>
    postJson<RerunMatchResult>('/api/mappings/rerun-match', masterSkus ? { master_skus: masterSkus } : {}),
};

/** Read-only helper for components: returns either the data or null on error,
 *  + an error message string for display. */
export interface AsyncResult<T> {
  loading: boolean;
  data: T | null;
  error: string | null;
}

export function unwrap<T>(r: ApiResp<T>): { data: T | null; error: string | null } {
  if (r.ok) return { data: r.data, error: null };
  return { data: null, error: `${r.error.code}: ${r.error.message}` };
}

// Re-export Product type for convenience in callers
export type { Product };
